import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  GlobalSearchControllerSearchModuleParamsEnum,
  GlobalSearchListDto,
  GlobalSearchResultDto,
  SearchableModule,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';
import { CACHE_TIME, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../constants';

export const GLOBAL_SEARCH_MIN_TERM_LENGTH = 3;
type GlobalSearchQuery = Parameters<
  typeof api.internalPortal.globalSearchControllerSearch
>[0];

type GlobalSearchModuleQuery = Parameters<
  typeof api.internalPortal.globalSearchControllerSearchModule
>[1];

const SEARCHABLE_MODULE_TO_API_PARAM: Record<
  SearchableModule,
  GlobalSearchControllerSearchModuleParamsEnum
> = {
  [SearchableModule.Organizations]:
    GlobalSearchControllerSearchModuleParamsEnum.Organizations,
  [SearchableModule.Vendors]:
    GlobalSearchControllerSearchModuleParamsEnum.Vendors,
  [SearchableModule.Audits]:
    GlobalSearchControllerSearchModuleParamsEnum.Audits,
  [SearchableModule.Tasks]: GlobalSearchControllerSearchModuleParamsEnum.Tasks,
  [SearchableModule.Projects]:
    GlobalSearchControllerSearchModuleParamsEnum.Projects,
};

export const useGlobalSearch = (
  query: GlobalSearchQuery,
  options?: Partial<UseQueryOptions<GlobalSearchResultDto>>
): UseQueryResult<GlobalSearchResultDto> => {
  const searchTerm = query.searchTerm.trim();
  const isEnabled = searchTerm.length >= GLOBAL_SEARCH_MIN_TERM_LENGTH;

  return useQuery({
    ...options,
    enabled: isEnabled && (options?.enabled ?? true),
    queryKey: [QueryKey.Resource.GlobalSearch, QueryKey.Operation.List, query],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.globalSearchControllerSearch({
        ...query,
        searchTerm,
      }),
  });
};

export const useInfiniteGlobalSearchModule = ({
  module,
  query,
  options,
}: {
  module?: SearchableModule;
  query: Omit<GlobalSearchModuleQuery, 'page'>;
  options?: Partial<
    UseInfiniteQueryOptions<
      GlobalSearchListDto,
      Error,
      InfiniteData<GlobalSearchListDto>
    >
  >;
}) => {
  const searchTerm = query.searchTerm.trim();
  const isEnabled =
    !!module && searchTerm.length >= GLOBAL_SEARCH_MIN_TERM_LENGTH;

  return useInfiniteQuery({
    ...options,
    enabled: isEnabled && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.GlobalSearch,
      module,
      QueryKey.Operation.Infinite,
      query,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    initialPageParam: DEFAULT_PAGE,
    queryFn: async ({ pageParam }) => {
      const page = pageParam as number;

      return api.internalPortal.globalSearchControllerSearchModule(
        SEARCHABLE_MODULE_TO_API_PARAM[module!],
        {
          ...query,
          searchTerm,
          page,
          size: query.size ?? DEFAULT_PAGE_SIZE,
        }
      );
    },
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
  });
};
